import React from 'react';
import { ListItem, Typography, Box, IconButton, Chip } from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, LocalShipping as PreorderIcon, ContentCopy as CloneIcon } from '@mui/icons-material';
import { useTranslation } from '../../hooks/useTranslation';
import { format } from 'date-fns';

function ToyListItem({ toy, userCurrency, onEdit, onDelete, onClone, getImageUrl }) {
  const { t } = useTranslation();

  const formatDate = (date) => {
    if (!date) return '';
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return '';
    return format(dateObj, 'yyyy/MM/dd');
  };

  return (
    <ListItem
      sx={{
        bgcolor: 'white',
        mb: 2,
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
        <Box sx={{ width: 80, height: 80, mr: 2, flexShrink: 0 }}>
          <img
            src={getImageUrl(toy.imageUrl, toy.name)}
            alt={toy.name}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: 8
            }}
          />
        </Box>
        
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            {toy.name}
          </Typography>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
            {toy.brand && (
              <Chip label={toy.brand} size="small" />
            )}
            {toy.series && (
              <Chip label={toy.series} size="small" variant="outlined" />
            )}
            {toy.type && (
              <Chip label={toy.type} size="small" variant="outlined" />
            )}
            {toy.scale && (
              <Chip label={toy.scale} size="small" variant="outlined" />
            )}
          </Box>
          
          <Typography variant="body2" color="text.secondary">
            {toy.code && `${t('common.code')}: ${toy.code} • `}
            {t('common.quantity')}: {toy.qty || 1} • {formatDate(toy.createdDate)}
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', ml: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', mb: 1 }}>
            {userCurrency} {toy.preorder_price && toy.preorder_price > 0 && !toy.createdDate 
              ? Math.round(toy.preorder_price).toLocaleString()
              : Math.round(toy.price || 0).toLocaleString()
            }
          </Typography>
          
          {toy.preorder_price && toy.preorder_price > 0 && !toy.createdDate && (
            <Chip
              icon={<PreorderIcon />}
              label={t('common.preorder')}
              size="small"
              color="warning"
              sx={{ mb: 1 }}
            />
          )}
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              size="small"
              onClick={() => onClone(toy)}
              sx={{ bgcolor: 'info.main', color: 'white', '&:hover': { bgcolor: 'info.dark' } }}
              title={t('common.clone')}
            >
              <CloneIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => onEdit(toy.id)}
              sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => onDelete(toy)}
              sx={{ bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </ListItem>
  );
}

export default ToyListItem; 